import React, { useState, useEffect, useCallback, useRef } from "react";
import { getJson, postJson } from "../../services/http";

// ── All colors are hardcoded inline — never rely on Bootstrap theme classes ──

const S = {
  black: "#111",
  dark: "#222",
  mid: "#555",
  light: "#888",
  white: "#fff",
  bg: "#fff",
  bgAlt: "#f4f4f4",
  border: "#ccc",
  green: "#0a7c3e",
  red: "#c0392b",
  blue: "#2563eb",
  orange: "#d35400",
  gray: "#6c757d",
};

const POLL_INTERVAL = 2000;

const COMPONENT_COLORS = {
  Auth: "#6f42c1", Tenants: "#0d6efd", Users: "#0dcaf0", Events: "#198754",
  Communication: "#fd7e14", Workflow: "#e6a817", Content: "#d63384",
  Reporting: "#20c997", Configuration: "#6c757d",
};

const STATUS_BADGE = {
  passed: { bg: S.green, label: "Passed" }, failed: { bg: S.red, label: "Failed" },
  skipped: { bg: S.gray, label: "Skipped" }, timedOut: { bg: S.orange, label: "Timed Out" },
  running: { bg: S.blue, label: "Running" }, error: { bg: S.red, label: "Error" },
};

const ERROR_PATTERNS = [
  { pattern: /not found.*available projects.*""/i, title: "Playwright config not found", message: "The Playwright configuration file could not be loaded.", fix: "Ensure playwright.config.js exists in the project root and @playwright/test is installed." },
  { pattern: /playwright.*not found|cannot find.*playwright|ENOENT.*playwright/i, title: "Playwright is not installed", message: "The Playwright binary was not found on this server.", fix: "SSH into the server and run:\n  cd /var/www/html/on_screen\n  npm install -D @playwright/test\n  npx playwright install chromium --with-deps" },
  { pattern: /ERR_CONNECTION_REFUSED/i, title: "Application not reachable", message: "The test browser could not connect to the application.", fix: "Ensure the app is running, or update BASE_URL in .env.test.local." },
  { pattern: /TEST_OWNER_USER.*TEST_OWNER_PASS|env vars are required/i, title: "Test credentials not configured", message: "TEST_OWNER_USER and TEST_OWNER_PASS are not set.", fix: "Add to your server .env:\n  TEST_OWNER_USER=your_username\n  TEST_OWNER_PASS=your_password" },
  { pattern: /Executable doesn.*exist|browserType\.launch|browser.*not.*install/i, title: "Chromium browser not installed", message: "Playwright needs a Chromium browser binary.", fix: "Run: npx playwright install chromium --with-deps" },
  { pattern: /ETIMEDOUT|ECONNABORTED|net::ERR_TIMED_OUT/i, title: "Connection timed out", message: "The test browser timed out.", fix: "Check that BASE_URL points to a reachable address." },
  { pattern: /401.*unauthorized|403.*forbidden|auth.*failed/i, title: "Authentication failed", message: "The test credentials were rejected.", fix: "Verify TEST_OWNER_USER and TEST_OWNER_PASS are valid." },
];

function diagnoseError(stderr, error) {
  const text = `${stderr || ""} ${error || ""}`;
  for (const p of ERROR_PATTERNS) { if (p.pattern.test(text)) return p; }
  return null;
}

export default function TestRunner({ tenantId }) {
  const [authorized, setAuthorized] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [suites, setSuites] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [groupBy, setGroupBy] = useState("component");
  const [activeRun, setActiveRun] = useState(null);
  const [runError, setRunError] = useState(null);
  const [runHistory, setRunHistory] = useState([]);
  const [expandedErrors, setExpandedErrors] = useState(new Set());
  const [expandedSuites, setExpandedSuites] = useState(new Set());
  const pollRef = useRef(null);

  useEffect(() => { getJson("/api/super-admin/check-access").then(() => setAuthorized(true)).catch(() => setAuthorized(false)); }, []);

  useEffect(() => {
    if (!authorized) return;
    setHealthLoading(true);
    getJson("/api/test-runner/health").then(setHealth).catch((err) => setHealth({ healthy: false, checks: [], error: err.message })).finally(() => setHealthLoading(false));
    getJson("/api/test-runner/suites").then((d) => { setSuites(d.suites || []); setSelected(new Set((d.suites || []).map((s) => s.id))); });
    getJson("/api/test-runner/runs").then((d) => setRunHistory(d.runs || []));
  }, [authorized]);

  const refreshHealth = () => {
    setHealthLoading(true);
    getJson("/api/test-runner/health").then(setHealth).catch((err) => setHealth({ healthy: false, checks: [], error: err.message })).finally(() => setHealthLoading(false));
  };

  const pollRun = useCallback((runId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const poll = () => {
      getJson(`/api/test-runner/runs/${runId}`).then((d) => {
        setActiveRun(d.run);
        if (d.run.status !== "running") {
          clearInterval(pollRef.current); pollRef.current = null;
          setRunError(d.run.status === "error" || d.run.status === "failed" ? diagnoseError(d.run.rawStderr, null) : null);
          getJson("/api/test-runner/runs").then((h) => setRunHistory(h.runs || []));
        }
      });
    };
    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL);
  }, []);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleRun = async () => {
    const ids = [...selected]; if (ids.length === 0) return;
    setRunError(null);
    try {
      const d = await postJson("/api/test-runner/run", { suiteIds: ids });
      if (d.runId) { setActiveRun({ id: d.runId, status: "running", suiteIds: ids, startedAt: new Date().toISOString() }); setExpandedErrors(new Set()); setExpandedSuites(new Set()); pollRun(d.runId); }
    } catch (err) {
      const msg = err?.data?.error || err?.message || String(err);
      setRunError(diagnoseError(msg, msg) || { title: "Failed to start test run", message: msg, fix: "Check the server logs." });
    }
  };

  const toggleSuite = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(suites.map((s) => s.id)));
  const selectNone = () => setSelected(new Set());
  const selectGroup = (key) => {
    const f = groupBy === "component" ? "component" : "type";
    const ids = suites.filter((s) => s[f] === key).map((s) => s.id);
    setSelected((p) => { const n = new Set(p); const all = ids.every((i) => n.has(i)); ids.forEach((i) => (all ? n.delete(i) : n.add(i))); return n; });
  };

  const grouped = {};
  const gf = groupBy === "component" ? "component" : "type";
  for (const s of suites) { const k = s[gf] || "Other"; if (!grouped[k]) grouped[k] = []; grouped[k].push(s); }

  const toggleError = (i) => setExpandedErrors((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const toggleSuiteExpand = (s) => setExpandedSuites((p) => { const n = new Set(p); n.has(s) ? n.delete(s) : n.add(s); return n; });
  const loadRun = (id) => { getJson(`/api/test-runner/runs/${id}`).then((d) => { setActiveRun(d.run); setExpandedErrors(new Set()); setExpandedSuites(new Set()); setRunError(d.run.status === "error" || d.run.status === "failed" ? diagnoseError(d.run.rawStderr, null) : null); }); };

  if (!authorized) {
    return (<div style={{ padding: 24 }}><div style={{ background: "#fdd", border: "2px solid #c00", borderRadius: 8, padding: 16, color: S.dark }}><strong>Access Denied</strong> — Super admin access required.</div></div>);
  }

  const isRunning = activeRun?.status === "running";
  const healthIssues = health?.checks?.filter((c) => !c.ok) || [];
  const hasHealthIssues = !health?.healthy && healthIssues.length > 0;

  // ── card wrapper style (forces white bg + dark text everywhere) ──
  const card = { background: S.bg, border: `1px solid ${S.border}`, borderRadius: 8, marginBottom: 16, color: S.dark };
  const cardHdr = { background: S.bgAlt, borderBottom: `1px solid ${S.border}`, padding: "10px 16px", fontWeight: 700, color: S.dark, borderRadius: "8px 8px 0 0" };

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: 16, color: S.dark }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h3 style={{ color: S.dark, margin: 0 }}>Playwright Test Runner</h3>
          <div style={{ color: S.mid, fontSize: 13, marginTop: 2 }}>Regression test suite for OnScreen PWA</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={refreshHealth} disabled={healthLoading} style={{ padding: "4px 12px", borderRadius: 4, border: `1px solid ${S.border}`, background: S.bg, color: S.dark, cursor: "pointer" }}>
            {healthLoading ? "..." : "Refresh Health"}
          </button>
          <button onClick={() => setGroupBy("component")} style={{ padding: "4px 12px", borderRadius: 4, border: "none", background: groupBy === "component" ? S.blue : S.bgAlt, color: groupBy === "component" ? S.white : S.dark, cursor: "pointer" }}>By Component</button>
          <button onClick={() => setGroupBy("type")} style={{ padding: "4px 12px", borderRadius: 4, border: "none", background: groupBy === "type" ? S.blue : S.bgAlt, color: groupBy === "type" ? S.white : S.dark, cursor: "pointer" }}>By Type</button>
        </div>
      </div>

      {/* Health loading */}
      {healthLoading && !health && (
        <div style={{ background: "#d0e8ff", border: "2px solid #2563eb", borderRadius: 8, padding: 12, color: S.dark, marginBottom: 16 }}>Checking test environment...</div>
      )}

      {/* Health issues */}
      {hasHealthIssues && (
        <div style={{ background: "#fff8e1", border: "2px solid #e6a817", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#7a5800", marginBottom: 12 }}>Environment issues detected — fix these before running tests</div>
          {health.checks.map((c) => (
            <div key={c.id} style={{ display: "flex", alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
              <span style={{ color: c.ok ? S.green : S.red, marginRight: 10, fontSize: 18, flexShrink: 0 }}>{c.ok ? "\u2713" : "\u2717"}</span>
              <div>
                <div style={{ fontWeight: 600, color: S.dark }}>{c.label}</div>
                {!c.ok && <div style={{ color: S.red, fontFamily: "monospace", fontSize: 13, marginTop: 4, background: "rgba(0,0,0,0.05)", padding: "6px 10px", borderRadius: 4, whiteSpace: "pre-wrap" }}>{c.detail}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Health OK */}
      {health && !hasHealthIssues && !healthLoading && (
        <div style={{ background: "#d4edda", border: "2px solid #0a7c3e", borderRadius: 8, padding: 12, color: "#0a5c2e", marginBottom: 16, fontWeight: 600 }}>
          All {health.checks?.length || 0} environment checks passed — ready to run tests.
        </div>
      )}

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {/* LEFT COLUMN */}
        <div style={{ flex: "0 0 340px", minWidth: 280 }}>
          {/* Suite selection */}
          <div style={card}>
            <div style={{ ...cardHdr, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Test Suites</span>
              <span>
                <button onClick={selectAll} style={{ border: `1px solid ${S.border}`, background: S.bg, color: S.dark, padding: "2px 8px", borderRadius: 3, cursor: "pointer", marginRight: 4, fontSize: 12 }}>All</button>
                <button onClick={selectNone} style={{ border: `1px solid ${S.border}`, background: S.bg, color: S.dark, padding: "2px 8px", borderRadius: 3, cursor: "pointer", fontSize: 12 }}>None</button>
              </span>
            </div>
            <div style={{ maxHeight: 500, overflowY: "auto" }}>
              {Object.entries(grouped).map(([gn, items]) => {
                const allC = items.every((s) => selected.has(s.id));
                const someC = items.some((s) => selected.has(s.id));
                return (
                  <div key={gn}>
                    <div onClick={() => selectGroup(gn)} style={{ display: "flex", alignItems: "center", padding: "8px 12px", background: S.bgAlt, borderBottom: `1px solid ${S.border}`, cursor: "pointer" }}>
                      <input type="checkbox" checked={allC} ref={(el) => { if (el) el.indeterminate = someC && !allC; }} readOnly style={{ marginRight: 8 }} />
                      <span style={{ background: COMPONENT_COLORS[gn] || S.gray, color: S.white, padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700, marginRight: 8 }}>{gn}</span>
                      <span style={{ marginLeft: "auto", color: S.mid, fontSize: 12 }}>{items.length} suite{items.length !== 1 ? "s" : ""}</span>
                    </div>
                    {items.map((su) => (
                      <div key={su.id} onClick={() => toggleSuite(su.id)} style={{ display: "flex", alignItems: "center", padding: "8px 12px 8px 28px", borderBottom: "1px solid #eee", cursor: "pointer", background: selected.has(su.id) ? "#e8f0fe" : S.bg }}>
                        <input type="checkbox" checked={selected.has(su.id)} readOnly style={{ marginRight: 8 }} />
                        <span style={{ color: S.dark }}>{su.name}</span>
                        <span style={{ marginLeft: "auto", color: S.mid, fontSize: 12 }}>{su.id}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            <div style={{ padding: 12, borderTop: `1px solid ${S.border}` }}>
              <button onClick={handleRun} disabled={isRunning || selected.size === 0} style={{ width: "100%", padding: "10px 0", borderRadius: 6, border: "none", background: isRunning ? S.blue : S.green, color: S.white, fontWeight: 700, fontSize: 15, cursor: isRunning || selected.size === 0 ? "not-allowed" : "pointer", opacity: selected.size === 0 ? 0.5 : 1 }}>
                {isRunning ? `Running ${activeRun.suiteIds?.length || 0} suite(s)...` : `Run ${selected.size} Suite${selected.size !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>

          {/* Run history */}
          {runHistory.length > 0 && (
            <div style={card}>
              <div style={cardHdr}>Run History</div>
              <div style={{ maxHeight: 250, overflowY: "auto" }}>
                {runHistory.map((r) => (
                  <button key={r.id} onClick={() => loadRun(r.id)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", padding: "8px 12px", border: "none", borderBottom: "1px solid #eee", background: activeRun?.id === r.id ? "#e8f0fe" : S.bg, cursor: "pointer", textAlign: "left", color: S.dark }}>
                    <div>
                      <div style={{ fontSize: 13, color: S.dark }}>{new Date(r.startedAt).toLocaleTimeString()}</div>
                      <div style={{ fontSize: 12, color: S.mid }}>{r.suiteIds?.length || 0} suites</div>
                    </div>
                    <span style={{ background: STATUS_BADGE[r.status]?.bg || S.gray, color: S.white, padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700 }}>{STATUS_BADGE[r.status]?.label || r.status}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* CLI */}
          <div style={card}>
            <div style={cardHdr}>CLI Commands</div>
            <div style={{ padding: 12, fontSize: 13, color: S.dark }}>
              <div style={{ marginBottom: 4 }}><code style={{ color: S.blue }}>npm test</code> <span style={{ color: S.mid }}>— all suites</span></div>
              <div style={{ marginBottom: 4 }}><code style={{ color: S.blue }}>npm run test:events</code> <span style={{ color: S.mid }}>— single suite</span></div>
              <div style={{ marginBottom: 4 }}><code style={{ color: S.blue }}>npm run test:debug</code> <span style={{ color: S.mid }}>— interactive</span></div>
              <div><code style={{ color: S.blue }}>npm run test:report</code> <span style={{ color: S.mid }}>— open HTML report</span></div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ flex: 1, minWidth: 300 }}>
          {/* Error banner */}
          {runError && (
            <div style={{ ...card, border: `2px solid ${S.red}` }}>
              <div style={{ padding: 16 }}>
                <div style={{ fontWeight: 700, color: S.red, fontSize: 16, marginBottom: 4 }}>{runError.title}</div>
                <div style={{ color: S.dark, fontSize: 13, marginBottom: 8 }}>{runError.message}</div>
                <div style={{ background: "#f9f9f9", border: `1px solid ${S.border}`, borderRadius: 6, padding: "8px 12px", fontFamily: "monospace", fontSize: 12, color: S.dark, whiteSpace: "pre-wrap" }}>
                  <strong style={{ color: S.green }}>How to fix:</strong>{"\n"}{runError.fix}
                </div>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!activeRun && !runError && (
            <div style={{ ...card, textAlign: "center", padding: "48px 16px" }}>
              <div style={{ fontSize: 48, color: S.light }}>&#9654;</div>
              <div style={{ color: S.dark, marginTop: 12 }}>Select test suites and click <strong>Run</strong> to start.</div>
              <div style={{ color: S.mid, fontSize: 13, marginTop: 4 }}>Tests can also be run via CLI: <code style={{ color: S.blue }}>npm test</code></div>
            </div>
          )}

          {/* Results */}
          {activeRun && (
            <>
              {/* Summary */}
              <div style={card}>
                <div style={{ padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div>
                      <span style={{ background: STATUS_BADGE[activeRun.status]?.bg || S.gray, color: S.white, padding: "4px 12px", borderRadius: 12, fontWeight: 700, fontSize: 13, marginRight: 8 }}>{STATUS_BADGE[activeRun.status]?.label || activeRun.status}</span>
                      <strong style={{ color: S.dark }}>Run Results</strong>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {activeRun.finishedAt && <div style={{ color: S.mid, fontSize: 13 }}>{Math.round((new Date(activeRun.finishedAt) - new Date(activeRun.startedAt)) / 1000)}s total</div>}
                      {activeRun.exitCode != null && activeRun.exitCode !== 0 && <div style={{ color: S.red, fontSize: 13 }}>Exit code: {activeRun.exitCode}</div>}
                    </div>
                  </div>

                  {isRunning && <div style={{ height: 4, background: "#ddd", borderRadius: 2, overflow: "hidden", marginBottom: 12 }}><div style={{ width: "100%", height: "100%", background: S.blue, animation: "pulse 1.5s infinite" }} /></div>}

                  {activeRun.summary && (
                    <div style={{ display: "flex", gap: 8, textAlign: "center" }}>
                      {[
                        { l: "Total", v: activeRun.summary.total, c: S.dark },
                        { l: "Passed", v: activeRun.summary.passed, c: S.green },
                        { l: "Failed", v: activeRun.summary.failed, c: S.red },
                        { l: "Skipped", v: activeRun.summary.skipped, c: S.gray },
                        { l: "Duration", v: activeRun.summary.duration ? `${(activeRun.summary.duration / 1000).toFixed(1)}s` : "-", c: S.dark },
                      ].map((x) => (
                        <div key={x.l} style={{ flex: 1, border: `1px solid ${S.border}`, borderRadius: 6, padding: 8, background: S.bg }}>
                          <div style={{ fontSize: 22, fontWeight: 700, color: x.c }}>{x.v}</div>
                          <div style={{ fontSize: 12, color: S.mid }}>{x.l}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!isRunning && !activeRun.summary && !activeRun.tests?.length && (
                    <div style={{ background: "#fdd", border: `1px solid ${S.red}`, borderRadius: 6, padding: 12, color: S.dark, marginTop: 8 }}>
                      <strong>No test results received.</strong> {activeRun.rawStderr ? "Check the raw output below." : "Playwright may not be installed correctly."}
                    </div>
                  )}
                </div>
              </div>

              {/* Test details */}
              {activeRun.tests?.length > 0 && (
                <div style={card}>
                  <div style={{ ...cardHdr, display: "flex", justifyContent: "space-between" }}>
                    <span>Test Details</span>
                    <span style={{ fontWeight: 400, color: S.mid, fontSize: 13 }}>{activeRun.tests.length} test(s)</span>
                  </div>
                  {renderTestsBySuite(activeRun.tests)}
                </div>
              )}

              {/* Raw output */}
              {activeRun.rawStderr && activeRun.status !== "running" && (
                <div style={card}>
                  <div style={cardHdr}>Raw Output</div>
                  <pre style={{ margin: 0, padding: 16, background: "#1e1e1e", color: "#d4d4d4", fontSize: 12, maxHeight: 400, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", borderRadius: "0 0 8px 8px" }}>{activeRun.rawStderr}</pre>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  function renderTestsBySuite(tests) {
    const bySuite = {};
    for (const t of tests) { const k = t.suite || "Unknown"; if (!bySuite[k]) bySuite[k] = []; bySuite[k].push(t); }

    return Object.entries(bySuite).map(([sn, st]) => {
      const passed = st.filter((t) => t.status === "passed").length;
      const failed = st.filter((t) => t.status === "failed" || t.status === "timedOut").length;
      const skipped = st.filter((t) => t.status === "skipped").length;
      const isExp = expandedSuites.has(sn);
      const hasFail = failed > 0;

      return (
        <div key={sn}>
          <div onClick={() => toggleSuiteExpand(sn)} style={{ display: "flex", alignItems: "center", padding: "8px 12px", background: hasFail ? "#fff0f0" : "#f0fff0", borderBottom: `1px solid ${S.border}`, cursor: "pointer" }}>
            <span style={{ marginRight: 8, color: S.dark }}>{isExp ? "\u25BC" : "\u25B6"}</span>
            <strong style={{ color: S.dark, fontSize: 13, marginRight: 8 }}>{sn}</strong>
            <span style={{ background: S.green, color: S.white, padding: "1px 8px", borderRadius: 10, fontSize: 11, marginRight: 4 }}>{passed}</span>
            {failed > 0 && <span style={{ background: S.red, color: S.white, padding: "1px 8px", borderRadius: 10, fontSize: 11, marginRight: 4 }}>{failed}</span>}
            {skipped > 0 && <span style={{ background: S.gray, color: S.white, padding: "1px 8px", borderRadius: 10, fontSize: 11 }}>{skipped}</span>}
            <span style={{ marginLeft: "auto", color: S.mid, fontSize: 12 }}>{st.length} test{st.length !== 1 ? "s" : ""}</span>
          </div>
          {(isExp || hasFail) && st.map((t, i) => {
            const gi = tests.indexOf(t);
            const fail = t.status === "failed" || t.status === "timedOut";
            const errExp = expandedErrors.has(gi);
            return (
              <div key={i}>
                <div onClick={() => fail && toggleError(gi)} style={{ display: "flex", alignItems: "center", padding: "6px 12px 6px 36px", borderBottom: "1px solid #eee", background: fail ? "#fff5f5" : S.bg, cursor: fail ? "pointer" : "default" }}>
                  <span style={{ color: t.status === "passed" ? S.green : t.status === "skipped" ? S.gray : S.red, marginRight: 8, fontSize: 16 }}>{t.status === "passed" ? "\u2713" : t.status === "skipped" ? "\u2014" : "\u2717"}</span>
                  <span style={{ color: S.dark, fontSize: 13 }}>{t.title}</span>
                  <span style={{ marginLeft: "auto", color: S.mid, fontSize: 12 }}>{t.duration ? `${(t.duration / 1000).toFixed(2)}s` : ""}</span>
                  {fail && <span style={{ marginLeft: 8, color: S.red, fontSize: 12 }}>{errExp ? "\u25B2" : "\u25BC"}</span>}
                </div>
                {fail && errExp && (
                  <div style={{ background: "#1e1e1e", padding: "12px 16px 12px 48px", fontSize: 12, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word", borderBottom: `1px solid ${S.border}` }}>
                    {t.error ? (
                      <>
                        <div style={{ color: "#f48771", fontWeight: 600, marginBottom: 4 }}>{t.error}</div>
                        {(() => { const d = diagnoseError("", t.error); return d ? (<div style={{ color: "#4ec9b0", margin: "8px 0", padding: "6px 8px", background: "#1a2a1a", borderRadius: 4 }}><strong>Likely cause:</strong> {d.title}{"\n"}<strong>Fix:</strong> {d.fix}</div>) : null; })()}
                        {t.errorStack && <div style={{ color: "#808080", marginTop: 8 }}>{t.errorStack}</div>}
                      </>
                    ) : (
                      <div style={{ color: "#f48771" }}>No error details captured. Check Raw Output below.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    });
  }
}
